<script setup lang="ts">
import { Ref, ref } from 'vue';
import InfoIcon from '@/dashboard/vue/components/icons/InfoIcon.vue';
import DeleteIcon from '@/dashboard/vue/components/icons/DeleteIcon.vue';
import ContextMenuButton from '@/dashboard/vue/components/common/ContextMenu/ContextMenuButton.vue';
import LinkIcon from '@/dashboard/vue/components/icons/LinkIcon.vue';
import ContextMenu from '@/dashboard/vue/components/common/ContextMenu/ContextMenu.vue';
import { Draft } from '@/dashboard/types/draft';
import { GroupMoveItems } from '@/dashboard/vue/components/public/PublicMainDraft/usePublicMainDraft';
import TransferIcon from '@/dashboard/vue/components/icons/TransferIcon.vue';
import MenuItem from '@/dashboard/vue/components/common/Menu/MenuItem.vue';
import Menu from '@/dashboard/vue/components/common/Menu/Menu.vue';
import { getProjectVisibilityType } from '@/dashboard/types/project_visibility';
import LockIcon from '@/dashboard/vue/components/icons/LockIcon.vue';
import TeamIcon from '@/dashboard/vue/components/icons/TeamIcon.vue';
import LabelIcon from '@/dashboard/vue/components/icons/LabelIcon.vue';
import FolderIcon from '@/dashboard/vue/components/icons/FolderIcon.vue';
import ImageIcon from '@/dashboard/vue/components/icons/ImageIcon.vue';

const props = defineProps<{
  draft: Draft;
  canDelete: boolean;
  canMove: boolean;
  groupMoveItems: GroupMoveItems[];
  menuStyle: () => { left?: string; right?: string };
  copyDraftAction: () => void;
  copyNameAction: () => void;
  deleteAction: (draft: Draft) => void;
  moveAction: (draft: Draft, projectId: number) => void;
}>();

const menu: Ref<HTMLDivElement | undefined> = ref(undefined);

defineExpose({ menu });
</script>

<template>
  <div class="fixed bottom-zero left-zero right-zero top-zero z-[1002] hidden bg-black/40 max-tablet:flex" />

  <div
    ref="menu"
    :style="props.menuStyle()"
    class="absolute z-[1003] flex flex-col gap-xs max-tablet:fixed max-tablet:bottom-s max-tablet:left-s max-tablet:right-s laptop:top-[12px]"
  >
    <Menu class="relative">
      <MenuItem :action="props.copyNameAction" class="z-[4]" :title-class="{ 'max-tablet:h-[58px]': true }">
        <div class="flex max-w-full items-center stroke-black text-black dark:stroke-white dark:text-white laptop:max-w-[300px]">
          <InfoIcon class="mr-[10px] min-w-[16px]" />
          <span class="text-caption-s overflow-hidden overflow-ellipsis whitespace-nowrap max-tablet:!text-[16px]">
            <span>{{ draft.name }}</span>
          </span>
        </div>
      </MenuItem>

      <MenuItem :action="props.copyDraftAction" class="z-[3]" :title-class="{ 'max-tablet:h-[58px]': true }">
        <div class="flex items-center stroke-black text-black dark:stroke-white dark:text-white">
          <LinkIcon class="mr-[10px] min-w-[16px]" />
          <span class="text-caption-s whitespace-nowrap max-tablet:!text-[16px]">Copy URL to this item</span>
        </div>
      </MenuItem>

      <MenuItem v-if="canMove" class="z-[2] max-tablet:hidden" :title-class="{ 'max-tablet:h-[58px]': true }">
        <div class="flex items-center gap-[10px] whitespace-nowrap">
          <TransferIcon class="min-w-[16px] stroke-black dark:stroke-white" />
          <span class="text-caption-s text-black dark:text-white">Move to...</span>
        </div>

        <template #menu>
          <Menu class="z-[1]">
            <template v-for="group in props.groupMoveItems" :key="group.workspace.id">
              <MenuItem>
                <div class="flex items-center gap-[10px] whitespace-nowrap">
                  <LabelIcon class="min-w-[16px] stroke-black dark:stroke-white" />
                  <span class="text-caption-s text-black dark:text-white">{{ group.workspace.name }}</span>
                </div>

                <template #menu>
                  <Menu class="z-[2]">
                    <template v-for="project in group.projects" :key="project.id">
                      <MenuItem :action="() => moveAction(draft, project.id)">
                        <div class="flex items-center gap-[10px] whitespace-nowrap">
                          <ImageIcon v-if="project.temporary" class="min-w-[16px] stroke-black dark:stroke-white" />
                          <LockIcon
                            v-else-if="getProjectVisibilityType(project) == 'private'"
                            :stroke-class="'stroke-black dark:stroke-white'"
                            :fill-class="'fill-black dark:fill-white'"
                            class="min-w-[16px]"
                          />
                          <TeamIcon v-else-if="getProjectVisibilityType(project) == 'team'" class="min-w-[16px] stroke-black dark:stroke-white" />
                          <FolderIcon v-else-if="getProjectVisibilityType(project) == 'link'" class="min-w-[16px] stroke-black dark:stroke-white" />
                          <span class="text-caption-s text-black dark:text-white">{{ project.name }}</span>
                        </div>
                      </MenuItem>
                    </template>
                  </Menu>
                </template>
              </MenuItem>
            </template>
          </Menu>
        </template>
      </MenuItem>

      <MenuItem v-if="canDelete" :action="() => props.deleteAction(draft)" class="z-[1]" :title-class="{ 'max-tablet:h-[58px]': true }">
        <div class="flex items-center stroke-red text-red">
          <DeleteIcon class="mr-[10px] min-w-[16px]" />
          <span class="text-caption-s whitespace-nowrap max-tablet:!text-[16px]">Delete</span>
        </div>
      </MenuItem>
    </Menu>

    <ContextMenu class="relative laptop:hidden">
      <template #body>
        <ContextMenuButton :need-dark="true" class="justify-center max-tablet:h-[58px]">
          <div class="flex items-center stroke-black text-center text-black dark:stroke-white dark:text-white">
            <span class="text-caption-s whitespace-nowrap max-tablet:!text-[16px]">Cancel</span>
          </div>
        </ContextMenuButton>
      </template>
    </ContextMenu>
  </div>
</template>
